<template>
  <v-container fluid>
    <h2>
      {{ $tc('energy_price_set', 2) }}
      <vf-tooltip :text="$t('new_energy_price_set')">
        <v-btn v-if="userLoaded && user.can('create_energypriceset')" fab text color="primary"
               to="/energy-price-set/create">
          <v-icon x-large>mdi-plus</v-icon>
        </v-btn>
      </vf-tooltip>
    </h2>

    <!-- Search bar -->
    <v-row align-v="center" class="search-criteria">
      <v-col md="6" cols="12">
        <vf-autocomplete
          clearable
          multiple
          :label="$tc('type', 2)"
          v-model="searchCriteria.energyPriceSetTypes"
          :items="energyPriceSetTypeOptions"
          prepend-inner-icon="mdi-filter"/>
      </v-col>

      <v-col md="6" cols="12">
        <vf-autocomplete
          clearable
          multiple
          :label="$tc('customer', 2)"
          v-model="searchCriteria.customers"
          :items="customers"
          item-value="id"
          item-text="name"
          prepend-inner-icon="mdi-office-building"/>
      </v-col>
    </v-row>

    <!-- page header -->
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold">{{ $t('results') }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <vf-tooltip :text="$t('search')">
          <v-btn fab text color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </vf-tooltip>
        <vf-tooltip :text="$t('reset_filters')">
          <v-btn fab text color="primary" @click="resetTable()" :disabled="tableControlsMatchDefaults">
            <v-icon>mdi-filter-remove</v-icon>
          </v-btn>
        </vf-tooltip>
        <vf-tooltip :text="$t('download')">
          <v-btn fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>

    <!-- Data Table -->
    <v-row id="energy-price-results" class="mt-3">
      <v-col>
        <v-data-table
          hide-default-footer
          class="vf-table mt-2"
          :headers="tableFields"
          :items="remoteData.items"
          :server-items-length="remoteData.totalRows"
          :sort-by.sync="remoteData.sortBy"
          :sort-desc.sync="remoteData.sortDesc"
          :loading="remoteData.isBusy"
          :loading-text="$t('loading')"
          @update:sort-by="initiateSearch()"
          @update:sort-desc="initiateSearch()">

          <template #item.description="row">
            <router-link :to="`energy-price-set/${row.item.id}`">{{ row.value }}</router-link>
          </template>

          <template #item.price="row">
            {{ row.value }} {{ row.item.currency }}
          </template>

          <template #item.most_recent_price="row">
            {{ row.item.most_recent_price }}
          </template>

          <template #item.customers="row">
            {{ map(row.item.customers, 'name').join(',') }}
          </template>

          <template #item.updated="row">
            <if-set :value="row.value">
              <template slot-scope="scope">
                <multi-time-display :time="scope.value"/>
              </template>
            </if-set>
          </template>

        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import VfTooltip from '@/components/shared/VfTooltip'
import LocationUtilitiesMixin from '@/components/location/LocationUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import EnergyPriceSetRecord from '@/orm/models/EnergyPriceSetRecord'
import { map } from 'lodash'

export default {
  name: 'EnergyPriceSetIndex',
  components: {
    VfAutocomplete,
    TablePaginationControls,
    MultiTimeDisplay,
    VfTooltip
  },
  mixins: [
    LocationUtilitiesMixin,
    CurrentUserMixin,
    RemoteDataTableMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: 'EnergyPriceSet',
      searchCriteria: this.getDefaultSearchCriteria(),
      tableFields: [
        { value: 'description', text: this.$tc('energy_price_set'), sortable: true },
        { value: 'type', text: this.$tc('type'), sortable: true },
        { value: 'unit_of_measure', text: this.$tc('unit_of_measure'), sortable: true },
        { value: 'currency', text: this.$tc('currency'), sortable: true },
        { value: 'most_recent_price', text: this.$tc('most_recent_price'), sortable: false },
        { value: 'updated', text: this.$tc('updated'), sortable: true },
        { value: 'customers', text: this.$tc('customer', 2), sortable: false }
      ],
      energyPriceSetTypeOptions: map(EnergyPriceSetRecord.types, type => {
        return { text: this.$t(type), value: type }
      })
    }
  },
  methods: {
    map: map,

    /**
     * Sets our data table back to it's oringal state before the user applied
     * filtering and paging changes
     *
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.refreshResults()
    },

    /**
     * Returns the default search criteria for this data table (DRY)
     *
     * @return Object
     */
    getDefaultSearchCriteria() {
      return {
        label: null,
        code: null,
        energyPriceSetTypes: [],
        customers: []
      }
    },

    /**
     * Constructs an object of key value pairs that represent the current search
     * criteria
     *
     * @return object
     */
    buildSearchCriteria() {
      const searchCriteria = {
        expand: 'customers',
        fields: 'id,code,description,type,unit_of_measure,currency,price_history,updated,customers.name',
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }

      // Filter by customer (if specified)
      if (this.searchCriteria.customers.length > 0) {
        searchCriteria.customer_id = this.searchCriteria.customers.join(',')
      }

      // Filter by energyPriceSet type (if specified)
      if (this.searchCriteria.energyPriceSetTypes.length > 0) {
        searchCriteria.type = this.searchCriteria.energyPriceSetTypes.join(',')
      }

      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }

      return searchCriteria
    }
  },
  mounted () {
    this.initiateSearch()
  },
  created () {
    this.updateBrowserTitle(this.$i18n.tc('energy_price_set', 2))
  }
}
</script>
